import React from "react";
import PropTypes from "prop-types";
import AIInsights from "./AIInsightsTailwind";

const MonthlyExpensesTab = ({ expenses, budgetComparison = [], dateRange, income = [] }) => {

  // Calculate budget totals
  const totalBudgeted = budgetComparison.reduce(
    (sum, item) => sum + item.budgeted,
    0,
  );
  const totalActual = budgetComparison.reduce(
    (sum, item) => sum + item.actual,
    0,
  );

  // Calculate income totals
  const totalIncome = income.reduce((sum, item) => sum + item.amount, 0);

  // Calculate total fixed expenses (all monthly expenses entered separately)
  const totalFixedExpenses = expenses.reduce((sum, item) => sum + item.amount, 0);

  // Separate subscriptions for dedicated management view
  const subscriptions = expenses.filter((expense) => expense.category === "Subscriptions");
  const totalSubscriptions = subscriptions.reduce((sum, item) => sum + item.amount, 0);

  // All budget comparison items are variable budget items (no fixed expenses in budget data)
  const variableBudget = budgetComparison.filter(item => !item.is_unbudgeted);
  const unbudgetedExpenses = budgetComparison.filter(item => item.is_unbudgeted);

  // Calculate totals for each section
  const totalVariableBudget = variableBudget.reduce((sum, item) => sum + item.budgeted, 0);
  const totalVariableActual = variableBudget.reduce((sum, item) => sum + item.actual, 0);
  const totalUnbudgeted = unbudgetedExpenses.reduce((sum, item) => sum + item.actual, 0);

  // Calculate remaining after fixed expenses
  const remainingAfterFixed = totalIncome - totalFixedExpenses;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="p-2 sm:p-4 max-w-full">
      {/* AI Insights */}
      <AIInsights dateRange={dateRange} />
      
      {/* Budget Planning Section - Income-First Flow */}
      {budgetComparison && budgetComparison.length > 0 && (
        <div className="mt-2 sm:mt-4">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">
            Budget Planning
          </h2>
          
          {/* Debug: Show budget comparison data */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mb-4 p-2 bg-yellow-100 text-xs">
              Debug: {budgetComparison.length} budget items, 
              Everything Else: {budgetComparison.find(item => item.is_unbudgeted) ? 'YES' : 'NO'}
            </div>
          )}

          {/* 1. Income Section */}
          <div className="mb-4 bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="text-md font-semibold text-green-800 mb-3">💰 Monthly Income</h3>
            <div className="flex flex-wrap justify-between items-center">
              <div className="text-center px-2 sm:px-4 w-full sm:w-auto mb-2 sm:mb-0">
                <div className="text-sm text-green-600">Total Income</div>
                <div className="font-bold text-lg sm:text-xl text-green-800">
                  {formatCurrency(totalIncome)}
                </div>
              </div>
              {income.length > 0 && (
                <div className="text-xs text-green-600 flex flex-wrap gap-2">
                  {income.map((item, index) => (
                    <span key={index} className="bg-green-100 px-2 py-1 rounded">
                      {item.name}: {formatCurrency(item.amount)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 2. Fixed Expenses Section */}
          {expenses.length > 0 && (
            <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="text-md font-semibold text-gray-800 mb-3">🏠 Fixed Expenses</h3>
              <div className="mb-3 bg-gray-100 p-3 rounded flex flex-wrap justify-between">
                <div className="text-center px-2 w-1/2 sm:w-auto mb-2 sm:mb-0">
                  <div className="text-xs text-gray-600">Total Fixed</div>
                  <div className="font-bold text-sm text-gray-800">{formatCurrency(totalFixedExpenses)}</div>
                </div>
                <div className="text-center px-2 w-1/2 sm:w-auto">
                  <div className="text-xs text-gray-600">Available for Budget</div>
                  <div className={`font-bold text-sm ${remainingAfterFixed < 0 ? "text-red-600" : "text-green-600"}`}>
                    {formatCurrency(remainingAfterFixed)}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {expenses
                      .sort((a, b) => {
                        // Sort by category first, then by name
                        if (a.category !== b.category) {
                          return a.category.localeCompare(b.category);
                        }
                        return a.name.localeCompare(b.name);
                      })
                      .map((item, index) => (
                      <tr key={index}>
                        <td className="px-2 sm:px-4 py-2 text-xs font-medium text-gray-900">{item.category}</td>
                        <td className="px-2 sm:px-4 py-2 text-xs text-gray-700">{item.name}</td>
                        <td className="px-2 sm:px-4 py-2 text-xs text-gray-700">{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 3. Variable Budget Section */}
          {variableBudget.length > 0 && (
            <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="text-md font-semibold text-blue-800 mb-3">📊 Variable Budget Items</h3>
              <div className="mb-3 bg-blue-100 p-3 rounded flex flex-wrap justify-between">
                <div className="text-center px-2 w-1/4 sm:w-auto mb-2 sm:mb-0">
                  <div className="text-xs text-blue-600">Available</div>
                  <div className="font-bold text-sm text-blue-800">{formatCurrency(Math.max(0, remainingAfterFixed))}</div>
                </div>
                <div className="text-center px-2 w-1/4 sm:w-auto mb-2 sm:mb-0">
                  <div className="text-xs text-blue-600">Budgeted</div>
                  <div className="font-bold text-sm text-blue-800">{formatCurrency(totalVariableBudget)}</div>
                </div>
                <div className="text-center px-2 w-1/4 sm:w-auto mb-2 sm:mb-0">
                  <div className="text-xs text-blue-600">Spent</div>
                  <div className={`font-bold text-sm ${totalVariableActual > totalVariableBudget ? "text-red-600" : "text-blue-800"}`}>
                    {formatCurrency(totalVariableActual)}
                  </div>
                </div>
                <div className="text-center px-2 w-1/4 sm:w-auto">
                  <div className="text-xs text-blue-600">Remaining</div>
                  <div className={`font-bold text-sm ${(totalVariableBudget - totalVariableActual) < 0 ? "text-red-600" : "text-green-600"}`}>
                    {formatCurrency(totalVariableBudget - totalVariableActual)}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        Budget
                      </th>
                      <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        Spent
                      </th>
                      <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        Left
                      </th>
                      <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {variableBudget.map((item, index) => (
                      <tr key={index} className={item.difference < 0 ? "bg-red-50" : ""}>
                        <td className="px-2 sm:px-4 py-2 text-xs font-medium text-gray-900">{item.category}</td>
                        <td className="px-2 sm:px-4 py-2 text-xs text-gray-500">{formatCurrency(item.budgeted)}</td>
                        <td className="px-2 sm:px-4 py-2 text-xs text-gray-500">{formatCurrency(item.actual)}</td>
                        <td className={`px-2 sm:px-4 py-2 text-xs ${item.difference < 0 ? "text-red-600 font-medium" : "text-green-600"}`}>
                          {formatCurrency(item.difference)}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-xs">
                          <div className="flex items-center">
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mr-2">
                              <div
                                className={`h-1.5 rounded-full ${
                                  item.percentUsed > 100 ? "bg-red-600" : item.percentUsed > 80 ? "bg-yellow-400" : "bg-green-600"
                                }`}
                                style={{ width: `${Math.min(100, item.percentUsed)}%` }}
                              ></div>
                            </div>
                            <span className="text-xs">{item.percentUsed.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 4. Unbudgeted Expenses Section */}
          {unbudgetedExpenses.length > 0 && (
            <div className="mb-4 bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="text-md font-semibold text-orange-800 mb-3">⚠️ Unbudgeted Expenses</h3>
              <div className="mb-3 bg-orange-100 p-3 rounded">
                <div className="text-center">
                  <div className="text-sm text-orange-600">Total Unbudgeted Spending</div>
                  <div className="font-bold text-lg text-orange-800">
                    {formatCurrency(totalUnbudgeted)}
                  </div>
                  <div className="text-xs text-orange-600 mt-1">
                    This spending reduces your available budget flexibility
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                {unbudgetedExpenses.map((item, index) => (
                  <div key={index} className="flex justify-between items-center text-xs bg-white p-2 rounded">
                    <span className="flex items-center">
                      <span className="text-orange-600 mr-1">⚠️</span>
                      <span className="font-medium text-gray-700">{item.category}</span>
                    </span>
                    <span className="text-orange-700 font-medium">{formatCurrency(item.actual)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 5. Budget Analysis */}
          <div className="mb-4 bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="text-md font-semibold text-purple-800 mb-3">🎯 Budget Analysis</h3>
            
            {/* Budget Flow */}
            <div className="mb-4 bg-white p-3 rounded border">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Budget Planning Flow</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-green-700">Monthly Income:</span>
                  <span className="font-bold text-green-700">{formatCurrency(totalIncome)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">- Fixed Expenses:</span>
                  <span className="font-bold text-gray-700">-{formatCurrency(totalFixedExpenses)}</span>
                </div>
                <div className="border-t pt-1 flex justify-between">
                  <span className="text-blue-700">= Available for Budget:</span>
                  <span className={`font-bold ${remainingAfterFixed < 0 ? "text-red-600" : "text-blue-700"}`}>
                    {formatCurrency(remainingAfterFixed)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Total Budgeted:</span>
                  <span className="font-bold text-blue-600">{formatCurrency(totalVariableBudget)}</span>
                </div>
                <div className="border-t pt-1 flex justify-between">
                  <span className="text-purple-700">Budget Surplus/Deficit:</span>
                  <span className={`font-bold text-lg ${(remainingAfterFixed - totalVariableBudget) < 0 ? "text-red-600" : "text-green-600"}`}>
                    {formatCurrency(remainingAfterFixed - totalVariableBudget)}
                  </span>
                </div>
              </div>
            </div>

            {/* Budget Status */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-xs text-purple-600">Budget Utilization</div>
                <div className="font-bold text-sm text-purple-800">
                  {remainingAfterFixed > 0 ? Math.round((totalVariableBudget / remainingAfterFixed) * 100) : 0}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-purple-600">Actual vs Budget</div>
                <div className={`font-bold text-sm ${totalVariableActual > totalVariableBudget ? "text-red-600" : "text-purple-800"}`}>
                  {totalVariableBudget > 0 ? Math.round((totalVariableActual / totalVariableBudget) * 100) : 0}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-purple-600">Unbudgeted</div>
                <div className="font-bold text-sm text-orange-600">
                  {formatCurrency(totalUnbudgeted)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-purple-600">Net Position</div>
                <div className={`font-bold text-sm ${(totalIncome - totalActual) < 0 ? "text-red-600" : "text-green-600"}`}>
                  {formatCurrency(totalIncome - totalActual)}
                </div>
              </div>
            </div>
          </div>

          {/* 6. Budget Summary */}
          <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-md font-semibold text-gray-800 mb-3">📈 Overall Summary</h3>
            <div className="flex flex-wrap justify-between">
              <div className="text-center px-2 sm:px-4 w-1/2 sm:w-auto mb-3 sm:mb-0">
                <div className="text-sm text-gray-600">Total Income</div>
                <div className="font-bold text-base sm:text-lg text-green-600">{formatCurrency(totalIncome)}</div>
              </div>
              <div className="text-center px-2 sm:px-4 w-1/2 sm:w-auto mb-3 sm:mb-0">
                <div className="text-sm text-gray-600">Total Spending</div>
                <div className="font-bold text-base sm:text-lg text-gray-800">{formatCurrency(totalActual + totalFixedExpenses)}</div>
              </div>
              <div className="text-center px-2 sm:px-4 w-1/2 sm:w-auto">
                <div className="text-sm text-gray-600">Final Net</div>
                <div className={`font-bold text-base sm:text-lg ${(totalIncome - totalActual - totalFixedExpenses) < 0 ? "text-red-600" : "text-green-600"}`}>
                  {formatCurrency(totalIncome - totalActual - totalFixedExpenses)}
                </div>
              </div>
              <div className="text-center px-2 sm:px-4 w-1/2 sm:w-auto">
                <div className="text-sm text-gray-600">Savings Rate</div>
                <div className={`font-bold text-base sm:text-lg ${(totalIncome - totalActual - totalFixedExpenses) < 0 ? "text-red-600" : "text-green-600"}`}>
                  {totalIncome > 0 ? Math.round(((totalIncome - totalActual - totalFixedExpenses) / totalIncome) * 100) : 0}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <h2 className="text-lg sm:text-xl font-bold text-blue-600 mb-4 mt-6">
        Subscription Management
      </h2>

      <div className="mb-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex flex-wrap justify-between items-center">
          <div className="w-full sm:w-auto mb-2 sm:mb-0">
            <h3 className="text-sm sm:text-md font-medium text-blue-700">
              Total Monthly Subscriptions: 
            </h3>
            <p className="text-xs text-blue-600 mt-1">
              {subscriptions.length} active subscriptions
            </p>
          </div>
          <div className="w-full sm:w-auto">
            <h3 className="text-lg sm:text-xl font-bold text-blue-800">
              {formatCurrency(totalSubscriptions)}
            </h3>
            <p className="text-xs text-blue-600 text-right">
              {totalFixedExpenses > 0 ? Math.round((totalSubscriptions / totalFixedExpenses) * 100) : 0}% of fixed expenses
            </p>
          </div>
        </div>
      </div>

      {subscriptions.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-sm">
            <thead>
              <tr className="bg-blue-100 text-blue-800 uppercase text-[10px] sm:text-xs leading-normal">
                <th className="py-2 sm:py-3 px-2 sm:px-6 text-left">Subscription Name</th>
                <th className="py-2 sm:py-3 px-2 sm:px-6 text-right">Monthly Cost</th>
                <th className="py-2 sm:py-3 px-2 sm:px-6 text-right">Annual Cost</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-[10px] sm:text-xs md:text-sm">
              {subscriptions
                .sort((a, b) => b.amount - a.amount) // Sort by amount descending
                .map((subscription, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-200 hover:bg-blue-50"
                >
                  <td className="py-1.5 sm:py-2 px-2 sm:px-6 text-left">
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900 truncate max-w-[120px] sm:max-w-[200px] md:max-w-none">
                        {subscription.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-1.5 sm:py-2 px-2 sm:px-6 text-right font-medium">
                    {formatCurrency(subscription.amount)}
                  </td>
                  <td className="py-1.5 sm:py-2 px-2 sm:px-6 text-right text-gray-500">
                    {formatCurrency(subscription.amount * 12)}
                  </td>
                </tr>
              ))}
              <tr className="bg-blue-50 border-t-2 border-blue-200">
                <td className="py-2 sm:py-3 px-2 sm:px-6 text-left text-[10px] sm:text-xs md:text-sm font-bold text-blue-800">
                  Total Monthly Subscriptions
                </td>
                <td className="py-2 sm:py-3 px-2 sm:px-6 text-right text-[10px] sm:text-xs md:text-sm font-bold text-blue-800">
                  {formatCurrency(totalSubscriptions)}
                </td>
                <td className="py-2 sm:py-3 px-2 sm:px-6 text-right text-[10px] sm:text-xs md:text-sm font-bold text-blue-800">
                  {formatCurrency(totalSubscriptions * 12)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-sm">No subscriptions found in your fixed expenses.</p>
          <p className="text-gray-400 text-xs mt-1">Add expenses with category "Subscriptions" to see them here.</p>
        </div>
      )}
    </div>
  );
};
MonthlyExpensesTab.propTypes = {
  expenses: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      category: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }),
  ).isRequired,
  budgetComparison: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
      budgeted: PropTypes.number.isRequired,
      actual: PropTypes.number.isRequired,
      difference: PropTypes.number.isRequired,
      percentUsed: PropTypes.number.isRequired,
    }),
  ),
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    label: PropTypes.string,
  }),
  income: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }),
  ),
};

export default MonthlyExpensesTab;
